/**
 * Return prompt: opened by scanning an item of an ACTIVE booking.
 *
 * Shows every physical line of the booking with the scanned one ticked.
 * Scanning more items ticks them off (optional). "End order" performs the same
 * state change as the End order button on the booking page (-> 'returned';
 * returned_at is set by trg_bookings_sync_returned_at). Unticked lines only
 * produce a warning, never a block. Blocked / inactive items can always be
 * returned. See docs/BARCODE_SCANNING_SPEC.md, "Flow D".
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, AlertTriangle, Circle } from 'lucide-react';
import ScanModal from './ScanModal';
import ScanInput from './ScanInput';
import { physicalLines, lineLabel, untickedLines, type PhysicalLine } from './scanLines';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useBarcodeScanning, useScanHandler } from '../../contexts/ScanContext';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { getBookingById, updateBooking } from '../../services/bookings.service';
import { insertAuditLog } from '../../services/bookingAuditLog.service';
import { resolveBarcode, findBookingsHoldingUnit, unitLabel } from '../../services/barcode.service';
import { emitBookingUpdated } from '../../lib/bookingEvents';

export interface ReturnScanStart {
  bookingId: string;
  unitId?: string;
  productTypeId?: string;
}

interface ReturnScanModalProps {
  start: ReturnScanStart;
  onClose: () => void;
  /** Called with a new start when the operator switches to another booking. */
  onSwitch: (next: ReturnScanStart) => void;
  onEnded?: (bookingId: string) => void;
}

export default function ReturnScanModal({ start, onClose, onSwitch, onEnded }: ReturnScanModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, merchantId, accessControl } = useAuth();
  const { showToast } = useToast();
  const { feedback } = useBarcodeScanning();
  const { formatDateTime } = useDateFormatter();

  const [booking, setBooking] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ticks, setTicks] = useState<Record<string, number>>({});
  const [otherBooking, setOtherBooking] = useState<{ label: string; start: ReturnScanStart; orderId: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmUnticked, setConfirmUnticked] = useState(false);
  const [saving, setSaving] = useState(false);

  const lines = useMemo<PhysicalLine[]>(() => physicalLines(booking?.booking_items), [booking]);

  const tick = (line: PhysicalLine) =>
    setTicks((prev) => ({ ...prev, [line.id]: Math.min(line.quantity, (prev[line.id] || 0) + 1) }));

  /** Tick the line matching a unit or product; returns false if nothing matched. */
  const tickFor = (all: PhysicalLine[], current: Record<string, number>, scan: { unitId?: string; productTypeId?: string }) => {
    const line = scan.unitId
      ? all.find((l) => l.inventory_item_id === scan.unitId)
      : all.find((l) => !l.inventory_item_id && l.product_type_id === scan.productTypeId && (current[l.id] || 0) < l.quantity)
        ?? all.find((l) => !l.inventory_item_id && l.product_type_id === scan.productTypeId);
    if (!line) return false;
    tick(line);
    return true;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setTicks({});
      setOtherBooking(null);
      setNotice(null);
      setConfirmUnticked(false);
      const { data, error: loadError } = await getBookingById(start.bookingId, accessControl);
      if (cancelled) return;
      if (loadError || !data) {
        setError(loadError || t('scan.lookupFailed', { error: '' }));
        setLoading(false);
        return;
      }
      setBooking(data);
      setLoading(false);
      tickFor(physicalLines((data as any).booking_items), {}, start);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start.bookingId, start.unitId, start.productTypeId]);

  const handleReturnScan = async (code: string) => {
    if (!booking) return;
    setNotice(null);
    setOtherBooking(null);
    const { data: res, error: resolveError } = await resolveBarcode(code, merchantId);
    if (resolveError || !res) {
      feedback('error');
      setNotice(t('scan.lookupFailed', { error: resolveError || '' }));
      return;
    }
    if (res.kind === 'unknown') {
      feedback('error');
      setNotice(t('scan.unknownBarcode', { code }));
      return;
    }
    if (res.kind === 'unit') {
      if (tickFor(lines, ticks, { unitId: res.unit.id })) {
        feedback('ok');
        return;
      }
      const { data: holding } = await findBookingsHoldingUnit(accessControl, res.unit.id);
      const other = holding.find((b) => b.booking_state === 'active' && b.id !== booking.id);
      feedback('error');
      if (other) {
        setOtherBooking({
          label: unitLabel(res.unit),
          orderId: other.order_id,
          start: { bookingId: other.id, unitId: res.unit.id },
        });
      } else {
        setNotice(t('scan.notInBooking', { item: `${res.unit.product_name} ${unitLabel(res.unit)}` }));
      }
      return;
    }
    if (tickFor(lines, ticks, { productTypeId: res.product.id })) {
      feedback('ok');
    } else {
      feedback('error');
      setNotice(t('scan.notInBooking', { item: res.product.product_name }));
    }
  };
  useScanHandler(handleReturnScan);

  const unticked = untickedLines(lines, ticks);
  const isActive = booking?.booking_state === 'active';
  const overdue = booking && new Date(booking.end_date).getTime() < Date.now();

  const handleEnd = async () => {
    if (!booking || !isActive) return;
    if (unticked.length > 0 && !confirmUnticked) {
      setConfirmUnticked(true);
      return;
    }
    setSaving(true);
    const { error: updateError } = await updateBooking(booking.id, { booking_state: 'returned' });
    setSaving(false);
    if (updateError) {
      feedback('error');
      setNotice(t('scan.endFailed', { error: updateError }));
      return;
    }

    const ticked = lines.filter((l) => (ticks[l.id] || 0) >= l.quantity);
    insertAuditLog({
      bookingId: booking.id,
      merchantId: booking.merchant_id,
      locationId: booking.location_id,
      changeType: 'status_changed',
      changeDetails: {
        field: 'booking_state',
        from: 'active',
        to: 'returned',
        source: 'barcode_scan',
        scanned: ticked.map(lineLabel),
        not_scanned: unticked.map(lineLabel),
      },
      userId: user?.id,
      userEmail: profile?.full_name || user?.email || null,
      triggeredBy: 'admin_panel',
    });

    feedback('ok');
    showToast(t('scan.bookingEnded', { orderId: booking.order_id }), 'success');
    emitBookingUpdated(booking.id);
    onEnded?.(booking.id);
    onClose();
  };

  const customer = booking ? `${booking.customer_firstname || ''} ${booking.customer_lastname || ''}`.trim() : '';

  return (
    <ScanModal
      title={booking ? t('scan.returnTitle', { orderId: booking.order_id }) : t('common.loading')}
      subtitle={booking ? customer : undefined}
      onClose={onClose}
      testId="scan-return-modal"
      footer={
        !booking ? (
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('scan.close')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="text-sm text-secondary hover:underline"
              style={{ marginRight: 'auto' }}
              onClick={() => { onClose(); navigate(`/bookings/${booking.id}/edit`); }}
            >
              {t('scan.openBooking')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEnd}
              disabled={!isActive || saving}
              data-testid="scan-return-end-button"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {confirmUnticked ? t('scan.endOrderAnyway') : t('bookings.endOrder')}
            </button>
          </>
        )
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-xl">
          <Loader2 className="w-8 h-8 text-blue animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : booking && (
        <div className="space-y-md">
          <div className="text-sm text-secondary flex flex-wrap gap-x-md gap-y-xs">
            <span>{formatDateTime(booking.start_date)} → {formatDateTime(booking.end_date)}</span>
            {overdue && <span className="badge badge-danger">{t('scan.overdue')}</span>}
          </div>

          {!isActive && (
            <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg text-sm text-warning">
              {t('scan.notActiveAnymore')}
            </div>
          )}

          <ul className="space-y-xs" data-testid="scan-return-lines">
            {lines.map((line) => {
              const done = (ticks[line.id] || 0) >= line.quantity;
              return (
                <li
                  key={line.id}
                  className={`flex items-center gap-sm p-sm rounded-lg border ${done ? 'border-success/40 bg-success/5' : 'border-primary'}`}
                >
                  {done ? <Check className="w-4 h-4 text-success flex-shrink-0" /> : <Circle className="w-4 h-4 text-secondary flex-shrink-0" />}
                  <span className={`text-sm flex-1 ${line.in_bundle ? 'pl-md' : ''}`}>{lineLabel(line)}</span>
                  {line.quantity > 1 && (
                    <span className="text-xs text-secondary">
                      {t('scan.ofQuantity', { done: ticks[line.id] || 0, total: line.quantity })}
                    </span>
                  )}
                  {!done && (
                    <button type="button" className="text-xs text-blue hover:underline" onClick={() => tick(line)}>
                      {t('scan.tickManually')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          <p className="text-xs text-secondary">{t('scan.returnHint')}</p>
          <ScanInput onSubmit={handleReturnScan} autoFocus testId="scan-return-input" />

          {notice && <p className="text-sm text-danger">{notice}</p>}

          {otherBooking && (
            <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg text-sm flex flex-wrap items-center gap-md">
              <span className="text-warning">{t('scan.belongsToOther', { item: otherBooking.label, orderId: otherBooking.orderId })}</span>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => onSwitch(otherBooking.start)}>
                {t('scan.switchTo', { orderId: otherBooking.orderId })}
              </button>
            </div>
          )}

          {confirmUnticked && unticked.length > 0 && (
            <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg" data-testid="scan-return-unticked-warning">
              <div className="flex items-start gap-sm">
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-warning">{t('scan.notAllScannedTitle')}</p>
                  <p className="text-secondary">{t('scan.notAllScannedReturn')}</p>
                  <ul className="list-disc list-inside text-secondary mt-xs">
                    {unticked.map((l) => <li key={l.id}>{lineLabel(l)}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </ScanModal>
  );
}
